import { tryOrNull } from '../tryOrNull';
import { AnalyticsNamespace } from './namespaces';
import type { DOMAttributes, MouseEvent as ReactMouseEvent } from 'react';

const EVENT_DELEGATION_NAMESPACE_ATTRIBUTE = 'data-analytics-namespace';
const EVENT_DELEGATION_ID_ATTRIBUTE = 'data-analytics-id';
const EVENT_DELEGATION_PROPS_ATTRIBUTE = 'data-analytics-props';
const ANALYTICS_ID_PROPERTY = 'ActionID';

type AnalyticsValue = string | number | boolean | undefined | null;
export type AnalyticsProps = Record<string, AnalyticsValue>;

type SendClickAnalyticWithNamespace = ({
	analytic,
	namespace,
}: {
	readonly analytic: AnalyticsProps;
	readonly namespace: AnalyticsNamespace;
}) => void;

function getAnalyticsPropsFromElementOrThrow(element: Element): AnalyticsProps {
	const analyticsStringProps = element.getAttribute(EVENT_DELEGATION_PROPS_ATTRIBUTE);

	if (!analyticsStringProps) {
		throw new Error('No analytics data found on element.');
	}

	return JSON.parse(analyticsStringProps);
}

const getAnalyticsPropsFromElementOrNull = tryOrNull(getAnalyticsPropsFromElementOrThrow);

function getAnalyticsIdFromElementOrThrow(element: Element): string {
	const analyticsId = element.getAttribute(EVENT_DELEGATION_ID_ATTRIBUTE);

	if (!analyticsId) {
		throw new Error('No analytics ID found on element.');
	}

	return analyticsId;
}

function getAnalyticsDataFromElementOrThrow(element: Element): AnalyticsProps {
	return {
		[ANALYTICS_ID_PROPERTY]: getAnalyticsIdFromElementOrThrow(element),
		...getAnalyticsPropsFromElementOrNull(element),
	} as const satisfies AnalyticsProps;
}

interface WithOnClickEventDelegationReturn extends Pick<DOMAttributes<HTMLElement>, 'onClickCapture'> {}

function isVariableAnalyticsNamespace(unknownVariable: unknown): unknownVariable is AnalyticsNamespace {
	return typeof unknownVariable === 'string' && unknownVariable in AnalyticsNamespace;
}

function getAnalyticsNamespaceFromElementOrThrow(element: Element): AnalyticsNamespace {
	const namespaceString = element.getAttribute(EVENT_DELEGATION_NAMESPACE_ATTRIBUTE);

	if (!namespaceString) {
		throw new Error('No analytics namespace found on element.');
	}

	if (!isVariableAnalyticsNamespace(namespaceString)) {
		throw new Error(`Invalid analytics namespace: '${namespaceString}'`);
	}

	return namespaceString;
}

export function withAnalyticNamespaceOnClickEventDelegation(
	sendClickAnalyticCallback: SendClickAnalyticWithNamespace,
): WithOnClickEventDelegationReturn {
	return {
		onClickCapture: function sendClickAnalytic(event: ReactMouseEvent<HTMLElement>): void {
			const clickedElement = 'target' in event && event.target instanceof window.Element ? event.target : null;
			if (!clickedElement) {
				return;
			}

			const analyticElement = clickedElement.closest(`[${EVENT_DELEGATION_ID_ATTRIBUTE}]`);
			if (!analyticElement) {
				return;
			}

			const analyticData = getAnalyticsDataFromElementOrThrow(analyticElement);

			const analyticsNamespaceElement = clickedElement.closest(`[${EVENT_DELEGATION_NAMESPACE_ATTRIBUTE}]`);
			if (!analyticsNamespaceElement) {
				return;
			}

			const analyticsNamespace = getAnalyticsNamespaceFromElementOrThrow(analyticsNamespaceElement);

			sendClickAnalyticCallback({
				analytic: analyticData,
				namespace: analyticsNamespace,
			} as const);
		},
	};
}

export interface WithAnalyticReturn {
	readonly [EVENT_DELEGATION_ID_ATTRIBUTE]: string;
	readonly [EVENT_DELEGATION_PROPS_ATTRIBUTE]?: string;
}

interface WithAnalyticsNamespaceReturn {
	readonly [EVENT_DELEGATION_NAMESPACE_ATTRIBUTE]: AnalyticsNamespace;
}
export function withAnalyticsNamespace(namespace: AnalyticsNamespace): WithAnalyticsNamespaceReturn {
	return {
		[EVENT_DELEGATION_NAMESPACE_ATTRIBUTE]: namespace,
	};
}

export function withAnalyticId(analyticId: string, analyticProps?: AnalyticsProps): WithAnalyticReturn {
	return {
		[EVENT_DELEGATION_ID_ATTRIBUTE]: analyticId,
		...(analyticProps && { [EVENT_DELEGATION_PROPS_ATTRIBUTE]: JSON.stringify(analyticProps) }),
	};
}
